import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import { products } from '@/mocks/companyData';
import { ASSETS } from '@/constants/assets';
import useSeo from '@/hooks/useSeo';

export default function DuraflowPage() {
  useSeo({
    title: 'Mangueiras e Componentes Hidráulicos Industriais | Duraflow',
    description: 'Fornecimento de mangueiras hidráulicas, conexões, válvulas, cilindros, bombas, acumuladores, filtros e componentes hidráulicos industriais.',
    keywords: 'componentes hidráulicos, mangueiras hidráulicas, bombas hidráulicas, válvulas hidráulicas, cilindros hidráulicos, acumuladores hidráulicos, DURAFLOW, Grupo Pilar',
    canonical: 'https://www.pilarmanutencao.com.br/equipamentos-e-componentes',
    ogUrl: 'https://www.pilarmanutencao.com.br/equipamentos-e-componentes',
    ogImage: ASSETS.logoduraflow,
  });

  return (
    <div className="min-h-screen bg-gp-graphite-light">
      <Navbar />
      <main>
        {/* Header Duraflow - Premium Industrial Banner */}
        <section className="relative pt-28 pb-10 bg-gp-graphite overflow-hidden">
          {/* Hero Background Image — hydraulic parts distributor commercial photography */}
          <div className="absolute inset-0">
            <img
              src="/images/EQUIPAMENTOS-E-COMPONENTES/HERO/HERO-DURAFLOW.webp"
              alt="Portfólio comercial de componentes hidráulicos para venda — bombas, motores, válvulas, engates rápidos, adaptadores, mangueiras e peças usinadas agrupadas por categoria"
              className="w-full h-full object-cover object-left"
            />
            {/* Overlay — darkens image so logo & text pop */}
            <div className="absolute inset-0 bg-gp-graphite/80"></div>
          </div>
          {/* Background: multi-layer premium industrial texture on right side */}
          <div className="absolute inset-0">
            {/* 1. Subtle dot grid — base texture */}
            <div
              className="absolute inset-0 opacity-[0.03]"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            />
            {/* 2. Primary diagonal orange stripes — wide, elegant */}
            <div
              className="absolute right-0 top-0 bottom-0 w-[55%] opacity-[0.04]"
              style={{
                backgroundImage: `repeating-linear-gradient(
                  -50deg,
                  transparent,
                  transparent 80px,
                  rgba(255, 123, 0, 0.22) 80px,
                  rgba(255, 123, 0, 0.22) 82px
                )`,
              }}
            />
            {/* 3. Secondary diagonal stripes — metallic grey, different angle for depth */}
            <div
              className="absolute right-0 top-0 bottom-0 w-[50%] opacity-[0.03]"
              style={{
                backgroundImage: `repeating-linear-gradient(
                  -35deg,
                  transparent,
                  transparent 100px,
                  rgba(200, 200, 200, 0.12) 100px,
                  rgba(200, 200, 200, 0.12) 101px
                )`,
              }}
            />
            {/* 4. Fine diagonal orange accent lines — closer spacing, technical feel */}
            <div
              className="absolute right-0 top-0 bottom-0 w-[40%] opacity-[0.05]"
              style={{
                backgroundImage: `repeating-linear-gradient(
                  -65deg,
                  transparent,
                  transparent 30px,
                  rgba(255, 123, 0, 0.15) 30px,
                  rgba(255, 123, 0, 0.15) 31px
                )`,
              }}
            />
            {/* 5. Geometric angular gradient — metallic blade / light reflection */}
            <div
              className="absolute right-0 top-0 bottom-0 w-[35%] opacity-[0.05]"
              style={{
                background: `linear-gradient(
                  160deg,
                  transparent 40%,
                  rgba(255, 123, 0, 0.06) 50%,
                  rgba(60, 60, 60, 0.08) 60%,
                  transparent 70%
                )`,
              }}
            />
            {/* 6. Horizontal micro-lines — industrial precision texture */}
            <div
              className="absolute right-[5%] top-0 bottom-0 w-[30%] opacity-[0.04]"
              style={{
                backgroundImage: `repeating-linear-gradient(
                  0deg,
                  transparent,
                  transparent 12px,
                  rgba(255, 255, 255, 0.06) 12px,
                  rgba(255, 255, 255, 0.06) 13px
                )`,
              }}
            />
            {/* 7. Reduced orange glow — soft, corporativo */}
            <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[600px] h-[600px] bg-df-orange/8 rounded-full blur-[150px]" />
          </div>

          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              {/* Logo + Branding row — tighter gap, more compact */}
              <div className="flex flex-col md:flex-row items-center gap-4 md:gap-5 mb-4">
                {/* Logo — ~20% larger */}
                <img
                  src={ASSETS.logoDuraflow}
                  alt="Logotipo DURAFLOW divisão de componentes hidráulicos"
                  className="w-44 h-44 md:w-56 md:h-56 object-contain"
                />
                {/* Orange vertical line — closer to logo */}
                <div className="hidden md:block w-px h-20 bg-df-orange/50 ml-0" />
                <div className="flex flex-col items-center md:items-start">
                  {/* Larger "Divisão de Equipamentos" */}
                  <span className="text-df-orange text-sm md:text-base uppercase tracking-[0.3em] font-medium">
                    Divisão de Equipamentos
                  </span>
                  <h1 className="font-display text-3xl md:text-5xl font-bold text-white uppercase leading-tight mt-1">
                    DURA<span className="text-df-orange">FLOW</span>
                  </h1>
                </div>
              </div>
              {/* Descriptive text — closer to branding */}
              <p className="text-gray-400 text-sm md:text-base leading-relaxed max-w-2xl">
                Componentes hidráulicos de alta qualidade para a indústria. Mangueiras, bombas, motores, cilindros e válvulas com padrão internacional.
              </p>
            </div>
          </div>
        </section>

        {/* Products */}
        <section className="relative py-16 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 relative z-10">
              <div>
                <h2 className="font-display text-xl md:text-2xl font-bold text-white uppercase tracking-wider">
                  Nossos <span className="text-df-orange">Produtos</span>
                </h2>
                <p className="text-gray-400 text-sm mt-2 max-w-lg">
                  Linha completa de componentes hidráulicos para manutenção, reposição e novos projetos.
                </p>
              </div>
              <a
                href="https://wa.me/5527993092922"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 md:mt-0 inline-flex items-center gap-2 border border-df-orange text-df-orange hover:bg-df-orange hover:text-white px-5 py-2.5 rounded-sm font-semibold text-sm uppercase tracking-wider transition-all duration-300 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg w-5 h-5 flex items-center justify-center"></i>
                Solicitar Catálogo
              </a>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 relative z-10">
              {products.map((product) => {
                const CardContent = (
                  <div className="group relative bg-gp-graphite rounded-lg overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-xl border border-transparent hover:border-gp-yellow/60">
                    <div className="relative h-[200px] md:h-[260px] overflow-hidden cursor-pointer">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gp-graphite via-transparent to-transparent opacity-70"></div>
                      <div className="absolute top-3 right-3 bg-df-orange text-white text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm">
                        {product.category}
                      </div>
                    </div>
                    <div className="p-4 md:p-5 cursor-pointer">
                      <h3 className="font-display text-lg md:text-xl font-bold text-white uppercase mb-2 group-hover:text-df-orange transition-colors duration-300">
                        {product.name}
                      </h3>
                      <p className="text-gray-400 text-xs md:text-sm leading-relaxed line-clamp-2">
                        {product.description}
                      </p>
                      <div className="mt-4 pt-4 border-t border-gray-700">
                        <a
                          href="https://wa.me/5527993092922"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-df-orange text-xs uppercase tracking-wider font-semibold hover:text-white transition-colors duration-300"
                        >
                          Consultar
                          <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                );

                return product.route ? (
                  <Link key={product.id} to={product.route} className="block">
                    {CardContent}
                  </Link>
                ) : (
                  <div key={product.id}>{CardContent}</div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 bg-gp-graphite">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-df-orange/20 to-gp-graphite"></div>
              <div className="absolute inset-0 bg-gp-graphite/60"></div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Precisa de componentes hidráulicos para seu projeto?
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Nosso time comercial está pronto para ajudar na seleção dos melhores componentes para sua aplicação, com suporte técnico e entrega em todo Brasil.
                </p>
                <a
                  href="https://wa.me/5527993092922"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-df-orange hover:bg-df-orange-dark text-white px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                    <i className="ri-whatsapp-line text-df-orange w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span>Chamar no WhatsApp</span>
                  <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Back to Home */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center justify-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para a Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}